import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';

const CRMPortal: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'choose' | 'role-login' | 'admin-login'>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [currentCrmUser, setCurrentCrmUser] = useState<any>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Automatically show the install banner when browser allows installation
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User installed PWA');
        }
        setDeferredPrompt(null);
        setShowInstallBanner(false);
      });
    } else {
      setShowInstallGuide(true);
    }
  };

  // Check if already logged in — show option to continue
  useEffect(() => {
    const crmUserStr = sessionStorage.getItem('aswCrmUser') || localStorage.getItem('aswCrmUser');
    const crmToken = sessionStorage.getItem('aswCrmToken') || localStorage.getItem('aswCrmToken');
    if (crmUserStr && crmToken) {
      try { setCurrentCrmUser(JSON.parse(crmUserStr)); } catch { /* ignore */ }
    } else {
      sessionStorage.removeItem('aswCrmUser');
      sessionStorage.removeItem('aswCrmToken');
      localStorage.removeItem('aswCrmUser');
      localStorage.removeItem('aswCrmToken');
      setCurrentCrmUser(null);
    }
  }, []);

  const loginAs = async (loginEmail: string, loginPassword: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.login(loginEmail, loginPassword);
      if (res.success) {
        // Store CRM user in tab-specific sessionStorage (and localStorage fallback)
        sessionStorage.setItem('aswCrmUser', JSON.stringify(res.user));
        sessionStorage.setItem('aswCrmToken', res.sessionToken);
        localStorage.setItem('aswCrmUser', JSON.stringify(res.user));
        localStorage.setItem('aswCrmToken', res.sessionToken);
        navigate('/crm/app');
      } else {
        setError('Wrong email or password. Please try again.');
      }
    } catch (e: any) {
      setError('Wrong email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loginAs(email, password);
  };

  const openAdminLogin = () => {
    setMode('admin-login');
    setEmail('asw@gmail.com');
    setPassword('');
    setError('');
  };

  const openRoleLogin = () => {
    setMode('role-login');
    setEmail('');
    setPassword('');
    setError('');
  };

  return (
    <div style={{
      minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ width: '100%', maxWidth: '520px' }}>
        {/* Install Banner */}
        {showInstallBanner && (
          <div style={{
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: 'white', borderRadius: '12px', padding: '14px 16px',
            boxShadow: '0 4px 16px rgba(2,132,199,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 24, gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>📲</span>
              <div style={{ fontSize: '13px', textAlign: 'left' }}>
                <strong style={{ display: 'block' }}>Install ASW CRM App</strong>
                Fast 1-click home screen access
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={handleInstallClick}
                style={{
                  background: 'white', color: '#0369a1', border: 'none',
                  borderRadius: '6px', padding: '6px 12px', fontSize: '12.5px',
                  fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                Install App
              </button>
              <button
                onClick={() => setShowInstallBanner(false)}
                style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
                  fontSize: '16px', cursor: 'pointer', padding: '0 4px'
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '16px', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(79,70,229,0.3)'
          }}>
            👥
          </div>
          <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#1E293B', margin: '0 0 6px', fontFamily: 'Inter, sans-serif' }}>
            ASW CRM Portal
          </h1>
          <p style={{ color: '#64748B', fontSize: '14px', margin: 0 }}>
            Choose how you want to access the CRM
          </p>
        </div>

        {currentCrmUser && mode === 'choose' && (
          <div style={{ background: '#E0E7FF', border: '1px solid #C7D2FE', borderRadius: '14px', padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#4338CA', fontWeight: 600 }}>Currently logged in as</div>
              <div style={{ fontSize: '15px', color: '#312E81', fontWeight: 800 }}>{currentCrmUser.name || 'User'} ({currentCrmUser.role || 'Admin'})</div>
            </div>
            <button
              onClick={() => navigate('/crm/app')}
              style={{ background: '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '13px', boxShadow: '0 2px 8px rgba(79,70,229,0.25)' }}
            >
              Continue →
            </button>
          </div>
        )}

        {mode === 'choose' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Master Admin Option */}
            <div
              onClick={openAdminLogin}
              style={{
                background: 'white', border: '2px solid #E2E8F0', borderRadius: '14px',
                padding: '24px 28px', cursor: 'pointer',
                transition: 'all 0.2s', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', gap: 20
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#4F46E5'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(79,70,229,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '12px', flexShrink: 0,
                background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, boxShadow: '0 4px 12px rgba(79,70,229,0.3)'
              }}>
                🔑
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: '16px', color: '#1E293B', marginBottom: 4, fontFamily: 'Inter, sans-serif' }}>
                  Master Admin
                </div>
                <div style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.4 }}>
                  Full CRM access — login to manage all leads, customers, departments, and users
                </div>
              </div>
              <div style={{ fontSize: 20, color: '#CBD5E1' }}>→</div>
            </div>

            {/* Role-based Login Option */}
            <div
              onClick={openRoleLogin}
              style={{
                background: 'white', border: '2px solid #E2E8F0', borderRadius: '14px',
                padding: '24px 28px', cursor: 'pointer',
                transition: 'all 0.2s', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', gap: 20
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#10B981'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(16,185,129,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '12px', flexShrink: 0,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
              }}>
                👤
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: '16px', color: '#1E293B', marginBottom: 4, fontFamily: 'Inter, sans-serif' }}>
                  Department Login
                </div>
                <div style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.4 }}>
                  Login with your team credentials — Sales, Loan, RTO, and other departments
                </div>
              </div>
              <div style={{ fontSize: 20, color: '#CBD5E1' }}>→</div>
            </div>

            {/* Install ASW CRM App Option */}
            <div
              onClick={handleInstallClick}
              style={{
                background: 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)',
                border: '2px solid #0284C7', borderRadius: '14px',
                padding: '22px 28px', cursor: 'pointer', color: 'white',
                transition: 'all 0.2s', boxShadow: '0 4px 16px rgba(2,132,199,0.3)',
                display: 'flex', alignItems: 'center', gap: 20
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(2,132,199,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(2,132,199,0.3)'; }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '12px', flexShrink: 0,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, backdropFilter: 'blur(4px)'
              }}>
                📲
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: '16px', color: 'white', marginBottom: 4, fontFamily: 'Inter, sans-serif' }}>
                  Install CRM App
                </div>
                <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.4 }}>
                  1-Click install ASW CRM as an App on your Mobile or Laptop home screen
                </div>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, background: 'white', color: '#0369A1', padding: '6px 12px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                Install ⚡
              </div>
            </div>

            {error && (
              <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px' }}>
                ⚠ {error}
              </div>
            )}
          </div>
        ) : (
          /* Master Admin OR Role Login Form */
          <div style={{ background: 'white', border: '2px solid #E2E8F0', borderRadius: '14px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button
                onClick={() => { setMode('choose'); setError(''); }}
                style={{ background: '#F1F5F9', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', color: '#475569' }}
              >
                ← Back
              </button>
              <div>
                <div style={{ fontWeight: 800, fontSize: '16px', color: '#1E293B', fontFamily: 'Inter, sans-serif' }}>
                  {mode === 'admin-login' ? 'Master Admin Login' : 'Department Login'}
                </div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>
                  {mode === 'admin-login' ? 'Enter admin master credentials' : 'Enter your CRM credentials'}
                </div>
              </div>
            </div>

            <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '10px 14px', border: '1.5px solid #E2E8F0',
                    borderRadius: '8px', fontSize: '14px', fontFamily: 'Inter, sans-serif',
                    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = mode === 'admin-login' ? '#4F46E5' : '#10B981'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    style={{
                      width: '100%', padding: '10px 40px 10px 14px', border: '1.5px solid #E2E8F0',
                      borderRadius: '8px', fontSize: '14px', fontFamily: 'Inter, sans-serif',
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = mode === 'admin-login' ? '#4F46E5' : '#10B981'}
                    onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#94A3B8',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {error && (
                <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' }}>
                  ⚠ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '11px', borderRadius: '8px', border: 'none', cursor: loading ? 'wait' : 'pointer',
                  background: mode === 'admin-login' ? 'linear-gradient(135deg, #4F46E5, #7C3AED)' : 'linear-gradient(135deg, #10B981, #059669)',
                  color: 'white',
                  fontWeight: 700, fontSize: '14px', fontFamily: 'Inter, sans-serif',
                  boxShadow: mode === 'admin-login' ? '0 4px 14px rgba(79,70,229,0.35)' : '0 4px 14px rgba(16,185,129,0.35)',
                  transition: 'opacity 0.2s',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Logging in...' : '→ Login to CRM'}
              </button>
            </form>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '12px', color: '#94A3B8' }}>
          ASW CRM · Role-based access system
        </p>

        {/* Install Guide Modal */}
        {showInstallGuide && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '20px'
          }}>
            <div style={{
              background: 'white', borderRadius: '16px', maxWidth: '460px', width: '100%',
              padding: '28px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', position: 'relative'
            }}>
              <button
                onClick={() => setShowInstallGuide(false)}
                style={{
                  position: 'absolute', top: '16px', right: '16px', background: '#F1F5F9',
                  border: 'none', borderRadius: '50%', width: '32px', height: '32px',
                  cursor: 'pointer', fontSize: '16px', color: '#64748B'
                }}
              >
                ✕
              </button>

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '36px', marginBottom: '8px' }}>📲</div>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#1E293B', fontWeight: 800 }}>
                  How to Install ASW CRM App
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748B' }}>
                  Fast 1-click access from your phone or desktop
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px' }}>
                <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '10px', borderLeft: '4px solid #0284C7' }}>
                  <div style={{ fontWeight: 700, color: '#0369A1', marginBottom: '2px' }}>📱 Mobile (Chrome / Android)</div>
                  <div style={{ color: '#475569', lineHeight: 1.4 }}>
                    Tap <strong>⋮ Menu</strong> at the top right &rarr; Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.
                  </div>
                </div>

                <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '10px', borderLeft: '4px solid #7C3AED' }}>
                  <div style={{ fontWeight: 700, color: '#6D28D9', marginBottom: '2px' }}>🍏 iPhone (Safari / iOS)</div>
                  <div style={{ color: '#475569', lineHeight: 1.4 }}>
                    Tap <strong>Share ⎘</strong> button at bottom &rarr; Scroll down & select <strong>"Add to Home Screen ➕"</strong>.
                  </div>
                </div>

                <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '10px', borderLeft: '4px solid #10B981' }}>
                  <div style={{ fontWeight: 700, color: '#047857', marginBottom: '2px' }}>💻 Desktop (Chrome / Edge)</div>
                  <div style={{ color: '#475569', lineHeight: 1.4 }}>
                    Click the <strong>Install ⊕</strong> icon in the top right browser address bar.
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowInstallGuide(false)}
                style={{
                  width: '100%', marginTop: '20px', padding: '11px', background: '#0284C7',
                  color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700,
                  fontSize: '14px', cursor: 'pointer'
                }}
              >
                Got It 👍
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMPortal;
